/* eslint react-hooks/exhaustive-deps: "warn" */

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './CoverageSection.module.scss';

type Translate = (key: string) => string;

interface CoverageRow {
  flag: string;
  country: string;
  identifier: string;
  authority: string;
  register: string;
}

const getRows = (t: Translate): CoverageRow[] => [
  {
    flag: 'us',
    country: t('United States'),
    identifier: 'EIN',
    authority: 'Internal Revenue Service',
    register: t('State file number · entity status · incorporation date · registered agent · officers'),
  },
  {
    flag: 'de',
    country: t('Germany'),
    identifier: t('VAT identification number'),
    authority: 'Bundeszentralamt für Steuern',
    register: t('Legal name · HRB or HRA number and issuing court · status · registered office · managing directors'),
  },
  {
    flag: 'es',
    country: t('Spain'),
    identifier: t('VAT identification number'),
    authority: 'Agencia Tributaria',
    register: t('Legal name · NIF · Registro Mercantil entry · legal form · status · administrators'),
  },
  {
    flag: 'gb',
    country: t('United Kingdom'),
    identifier: t('VAT registration number'),
    authority: 'HM Revenue & Customs',
    register: t('Company number · status · filing history · accounts · officers · persons with significant control'),
  },
  {
    flag: 'nl',
    country: t('Netherlands'),
    identifier: t('VAT identification number'),
    authority: 'Belastingdienst',
    register: t('KvK number · legal form · status · branches · group relationships'),
  },
  {
    flag: 'it',
    country: t('Italy'),
    identifier: t('VAT identification number'),
    authority: 'Agenzia delle Entrate',
    register: t('REA and Registro Imprese entry · legal form · status · officers'),
  },
  {
    flag: 'ch',
    country: t('Switzerland'),
    identifier: t('UID / MWST number'),
    authority: 'Federal Statistical Office',
    register: t('Zefix entry · legal form · status · registered office · authorised signatories'),
  },
  {
    flag: 'in',
    country: t('India'),
    identifier: 'GSTIN',
    authority: 'Goods and Services Tax Network',
    register: t('CIN · MCA status · incorporation date · directors · registered charges'),
  },
  {
    flag: 'au',
    country: t('Australia'),
    identifier: 'ABN',
    authority: 'Australian Business Register',
    register: t('ASIC status · entity type · officeholders · historical names'),
  },
  {
    flag: 'br',
    country: t('Brazil'),
    identifier: 'CNPJ',
    authority: 'Receita Federal',
    register: t('Commercial board registration · legal form · status · officers'),
  },
];

export const CoverageSection = () => {
  const { t } = useInt();

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('Every jurisdiction leaves a different gap')}
            description={t(
              'The tax authority answers one question. The company register answers the rest. What that actually looks like, country by country.',
            )}
          />
        </div>

        <div className={styles.table}>
          <div className={styles.tableHead}>
            <div>{t('Jurisdiction and identifier')}</div>
            <div>{t('Issuing authority')}</div>
            <div>{t('Added from the company register')}</div>
          </div>

          {getRows(t).map((row) => (
            <div key={row.country} className={styles.row}>
              <div className={styles.jurisdiction}>
                <Flag code={row.flag} className={styles.flag} rounded />
                <span>
                  <b>{row.country}</b>
                  <em>{row.identifier}</em>
                </span>
              </div>

              <div className={styles.authority}>{row.authority}</div>

              <div className={styles.register}>{row.register}</div>
            </div>
          ))}
        </div>

        <div className={styles.bigCta}>
          <div className={styles.bigCtaText}>
            <h3>{t('Every VAT and GST number format, country by country')}</h3>
            <p>
              {t(
                'Structure, local-language name, worked example and registration threshold for 110 jurisdictions, each verified against the issuing authority — with a format validator that runs in your browser. No account, nothing leaves the page.',
              )}
            </p>
          </div>

          <div className={styles.bigCtaAction}>
            <Button
              type="blue"
              size="lg"
              href="/free-vat-number-checker-and-formats-by-country"
            >
              {t('View all VAT number formats →')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
